import React, { useEffect, useState } from 'react';
import axios from 'axios';
import SettingsGroup, { SettingsBlock } from './editorial/SettingsGroup';
import {
  EditorialToggle,
  EditorialCheckbox,
  EditorialSlider,
  FieldLabel,
} from './editorial/EditorialControls';
import { LockedEmailCheckbox } from './LockedEmailChannel';

const NUDGE_TYPES = [
  ['pace_overrun', 'Spending pace alerts'],
  ['bill_detected', 'Recurring bill notices'],
  ['payday', 'Payday redirect suggestions'],
  ['vision_milestone', 'Goal milestones'],
];

const EngagementSettings = () => {
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios
      .get('/api/engagement/preferences')
      .then((res) => {
        if (res.data.success) setPrefs(res.data.preferences);
      })
      .catch(console.error);
  }, []);

  const update = async (patch) => {
    setSaving(true);
    try {
      const res = await axios.put('/api/engagement/preferences', patch);
      if (res.data.success) setPrefs(res.data.preferences);
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  if (!prefs) return null;

  const toggle = (key) => update({ [key]: !prefs[key] });

  return (
    <SettingsGroup
      title="Aura nudges"
      description="Event-based suggestions only — not daily spam. You always accept or decline."
    >
      <SettingsBlock>
        <p className="text-label text-secondary mb-4">Suggestion types</p>
        <div className="space-y-0 hairline-list border-t border-hairline">
          {NUDGE_TYPES.map(([key, label]) => (
            <EditorialToggle
              key={key}
              id={`nudge-${key}`}
              label={label}
              checked={!!prefs[key]}
              onChange={() => toggle(key)}
            />
          ))}
        </div>
      </SettingsBlock>

      <SettingsBlock className="border-t border-hairline">
        <EditorialSlider
          id="max-nudges"
          label="Max nudges per week"
          min={0}
          max={10}
          value={prefs.max_nudges_per_week ?? 3}
          onChange={(v) => update({ max_nudges_per_week: v })}
        />
      </SettingsBlock>

      <SettingsBlock className="border-t border-hairline">
        <p className="text-label text-secondary mb-3">Quiet hours (24h, your local time)</p>
        <div className="grid grid-cols-2 gap-4 max-w-xs">
          <div>
            <FieldLabel htmlFor="quiet-start">Start</FieldLabel>
            <input
              id="quiet-start"
              type="number"
              min={0}
              max={23}
              value={prefs.quiet_hours_start ?? 21}
              onChange={(e) => update({ quiet_hours_start: Number(e.target.value) })}
              className="input-editorial figure"
            />
          </div>
          <div>
            <FieldLabel htmlFor="quiet-end">End</FieldLabel>
            <input
              id="quiet-end"
              type="number"
              min={0}
              max={23}
              value={prefs.quiet_hours_end ?? 8}
              onChange={(e) => update({ quiet_hours_end: Number(e.target.value) })}
              className="input-editorial figure"
            />
          </div>
        </div>
      </SettingsBlock>

      <SettingsBlock className="border-t border-hairline">
        <p className="text-label text-secondary mb-3">Channels</p>
        <EditorialCheckbox
          id="channel-in-app"
          label="In-app"
          checked={!!prefs.channel_in_app}
          onChange={(v) => update({ channel_in_app: v })}
        />
        <LockedEmailCheckbox
          id="channel-email"
          label="Email (opt-in)"
          checked={!!prefs.channel_email}
        />
      </SettingsBlock>

      {saving && <p className="text-label text-secondary py-2">Saving…</p>}
    </SettingsGroup>
  );
};

export default EngagementSettings;
